import { Auth, type CognitoUser } from '@aws-amplify/auth'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { CognitoIdToken } from 'amazon-cognito-identity-js'
import axios, { setAccessToken, setSpoofToken } from 'api/apiCommon'
import { logout } from 'features/authentication/auth.slice'
import { type ForgotPasswordForm } from 'common/authentication/forgotPassword/ForgotPassword'

import { type RegisterForm } from './register/Register'
import { type LoginForm } from './login/Login'

export const refreshToken = async (): Promise<string> => {
  const session = await Auth.currentSession()
  return session.getIdToken().getJwtToken()
}

export const checkTokenIsExpired = (token: string): boolean => {
  const decodedToken = new CognitoIdToken({ IdToken: token })
  if (decodedToken) {
    return decodedToken.getExpiration() < Math.floor(Date.now() / 1000)
  }
  return true
}

export const getAuthUser = createAsyncThunk('auth/getAuthUser', async () => {
  return await Auth.currentAuthenticatedUser()
    .then(async (user: CognitoUser) => {
      const token = await refreshToken()
      setAccessToken(token)
      return user
    })
    .catch(() => {
      // no current user
      return null
    })
})

export const startSpoofingSession = createAsyncThunk(
  '/users/impersonation-token',
  async (request: { userId: string }): Promise<string> => {
    return await axios
      .post('/users/impersonation-token', request)
      .then((response) => {
        setSpoofToken(response)
        return response
      })
      .catch((err) => {
        return err
      })
  },
)

export const signIn = createAsyncThunk(
  'auth/signIn',
  async ({ email, password }: LoginForm): Promise<{ user: CognitoUser; isAuthenticated: boolean }> => {
    return await new Promise((resolve, reject) => {
      Auth.signIn(email, password)
        .then(async (user: CognitoUser) => {
          if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
            resolve({ user, isAuthenticated: false })
            return
          }
          await refreshToken().then((token) => {
            setAccessToken(token)
            resolve({ user, isAuthenticated: true })
          })
        })
        .catch((err) => {
          switch (err.name) {
            case 'NotAuthorizedException':
            case 'UserNotFoundException':
              reject(new Error('Korisnik sa datim mejlom / šifrom ne postoji.'))
              break
            default:
              reject(err)
              break
          }
        })
    })
  },
)

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async ({ email }: ForgotPasswordForm) => {
    return await Auth.forgotPassword(email)
  },
)

interface ResetPasswordPayload {
  user: CognitoUser | null
  password: string
}
export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ user, password }: ResetPasswordPayload) => {
    return await new Promise<CognitoUser>((resolve, reject) => {
      Auth.completeNewPassword(user, password)
        .then(async (user: CognitoUser) => {
          await Auth.signOut()
          await Auth.signIn(user.getUsername(), password)
          await refreshToken().then((token) => {
            setAccessToken(token)
            resolve(user)
          })
        })
        .catch((err) => {
          console.error('reset password err: ', err)
          reject(err)
        })
    })
  },
)

export const signOut = createAsyncThunk('auth/signOut', async (_, { dispatch }) => {
  await Auth.signOut()
  // clear redux state
  dispatch(logout())
})

export const register = createAsyncThunk(
  'auth/register',
  async (registrationValues: RegisterForm) => await axios.post('/users/register-vendor', registrationValues),
)

export const sendActivationEmail = createAsyncThunk('auth/sendActivationEmail', async () => {
  await axios.put('/users/vendor-setup')
})

interface ActivateVendorPayload {
  email: string
  token: string
}

export const activateVendor = createAsyncThunk(
  'auth/activateVendor',
  async (activateVendorPayload: ActivateVendorPayload) => {
    await axios.put('/users/activate-vendor', activateVendorPayload)
  },
)

export const sendVerificationRequest = createAsyncThunk('auth/requestVerification', async () => {
  await axios.put('/vendors/request-verification')
})
